'use client';
/**
 * Editor state. The document itself lives inside a `History` instance from @freealbum/document —
 * every mutation goes through `apply(command)` so undo/redo and autosave stay consistent.
 * The store keeps a reference to the *current* immutable document for rendering.
 */
import { create } from 'zustand';
import { canSelectTrayEntry, mergeTrayPhotos, normalizePhoto, queueTrayUpload, sortPhotoTray, type PhotoTrayEntry } from './photo-tray';
import { History, normalizeSingles, referencedPhotoIds, sameTags, type Adjustments, type AlbumDocument, type Command, type Element, type PhotoInfo } from '@freealbum/document';
import type { AlbumDto, FontDto, ShareLinkDto } from '@freealbum/shared';
import type { EditorInit, PanelTab, PhotoDrag, PhotoFilter, PhotoSort, SaveState, Selection, Target, UploadItem } from './types';

export const ZOOM_LIMITS = { min: 0.1, max: 4 } as const;

interface EditorState {
  ready: boolean;
  albumId: string;
  album: AlbumDto | null;
  title: string;
  doc: AlbumDocument;
  version: number;
  history: History;
  canUndo: boolean;
  canRedo: boolean;

  photos: Record<string, PhotoInfo>;
  photoIds: string[];
  photoTray: PhotoTrayEntry[];

  currentTarget: Target;
  selection: Selection;

  /** px per mm actually used to draw the canvas */
  zoom: number;
  zoomMode: 'fit' | 'manual';
  fitZoom: number;
  pan: { x: number; y: number };

  panelTab: PanelTab;
  /** Mobile sheet visibility; the desktop sidebar is always visible. */
  panelOpen: boolean;
  /** A text field inside the phone's tool sheet has focus; the sheet grows to make room. */
  sheetFocus: boolean;

  editingTextId: string | null;
  cropElementId: string | null;
  /** photo element open in the photo editor dialog */
  photoEditorId: string | null;
  /** a copied "look" — adjustments to paste onto other photos; session-only */
  adjustmentsClipboard: Adjustments | null;
  /** photo element waiting for a tray pick ("replace" in the contextual toolbar) */
  replaceForElementId: string | null;

  dirty: boolean;
  saveState: SaveState;
  conflict: { version: number; doc: AlbumDocument } | null;

  uploads: Record<string, UploadItem>;
  photoDrag: PhotoDrag | null;
  uploadRecovery: 'idle' | 'checking' | 'error';

  /** fonts the user uploaded; merged into the font picker and injected as @font-face */
  userFonts: FontDto[];
  shareLinks: ShareLinkDto[];

  layoutScope: 'spread' | 'page';
  layoutPage: 'left' | 'right';
  photoFilter: PhotoFilter;
  photoSort: PhotoSort;
  /** Narrow the tray to one tag, on top of `photoFilter`. */
  photoTag: string | null;
  /** Stable tile keys, including files that failed before receiving a server photo ID. */
  traySelection: string[];

  // ── actions ─────────────────────────────────────────────
  init: (payload: EditorInit & { album?: AlbumDto | null }) => void;
  apply: (cmd: Command, opts?: { coalesceKey?: string }) => void;
  endCoalescing: () => void;
  undo: () => void;
  redo: () => void;
  resetDocument: (doc: AlbumDocument, version: number) => void;
  /**
   * Flip the "first page stands alone" invariant. It is not a `Command` — it changes what a spread
   * *is*, and `normalizeSingles` rewrites the whole array — so it restarts the history rather than
   * pushing a step that undo could not honestly reverse.
   */
  setFirstPageSingle: (on: boolean) => void;

  setTitle: (title: string) => void;
  setTarget: (target: Target) => void;
  select: (ids: string[], opts?: { target?: Target; additive?: boolean }) => void;
  toggleSelect: (id: string) => void;
  clearSelection: () => void;

  setZoom: (zoom: number) => void;
  setFitZoom: (zoom: number) => void;
  zoomBy: (factor: number) => void;
  zoomToFit: () => void;
  setPan: (pan: { x: number; y: number }) => void;

  setPanelTab: (tab: PanelTab) => void;
  setPanelOpen: (open: boolean) => void;
  setSheetFocus: (focused: boolean) => void;

  setEditingText: (id: string | null) => void;
  setCropElement: (id: string | null) => void;
  setPhotoEditor: (id: string | null) => void;
  setAdjustmentsClipboard: (adjustments: Adjustments | null) => void;
  setReplaceFor: (id: string | null) => void;

  markSaving: () => void;
  markSaved: (version: number) => void;
  markSaveError: () => void;
  setConflict: (c: { version: number; doc: AlbumDocument } | null) => void;

  setUpload: (item: UploadItem) => void;
  clearUpload: (id: string) => void;
  setPhotos: (photos: PhotoInfo[]) => void;
  upsertPhoto: (photo: PhotoInfo) => void;
  removePhoto: (id: string) => void;

  setPhotoDrag: (drag: PhotoDrag | null) => void;
  setUserFonts: (fonts: FontDto[]) => void;
  setShareLinks: (links: ShareLinkDto[]) => void;
  setLayoutScope: (scope: 'spread' | 'page') => void;
  setLayoutPage: (page: 'left' | 'right') => void;
  setPhotoFilter: (f: PhotoFilter) => void;
  setPhotoSort: (s: PhotoSort) => void;
  setPhotoTag: (tag: string | null) => void;
  /** Replace tag lists (server answer or optimistic guess) without touching URLs or untouched tiles. */
  applyPhotoTags: (updates: readonly { id: string; tags: string[] }[]) => void;
  toggleTraySelection: (id: string, additive?: boolean) => void;
  selectTrayPhotos: (ids: string[]) => void;
  clearTraySelection: () => void;
}

/** Placeholder document used before `init` runs; never rendered (guarded by `ready`). */
const EMPTY_DOC = {
  version: 1,
  format: 'sq20',
  page: { widthMm: 200, heightMm: 200 },
  bleedMm: 3,
  firstPageSingle: true,
  meta: { title: '', subtitle: '', lang: 'he' },
  theme: { id: 'family', palette: ['#ffffff', '#1b1b1f', '#c8553d'], headingFont: 'Frank Ruhl Libre', bodyFont: 'David Libre', coverBackground: null, pageBackground: null },
  cover: { enabled: true, spineWidthMm: 6, background: { type: 'color', color: '#1b1b1f' }, elements: [] },
  spreads: [{ id: 'placeholder', layoutId: null, single: null, background: { type: 'color', color: '#ffffff' }, backgroundRight: null, elements: [], note: null, tags: [] }],
} as unknown as AlbumDocument;

export const useEditor = create<EditorState>((set, get) => ({
  ready: false,
  albumId: '',
  album: null,
  title: '',
  doc: EMPTY_DOC,
  version: 0,
  history: new History(EMPTY_DOC),
  canUndo: false,
  canRedo: false,

  photos: {},
  photoIds: [],
  photoTray: [],

  currentTarget: 0,
  selection: { target: 0, ids: [] },

  zoom: 1,
  zoomMode: 'fit',
  fitZoom: 1,
  pan: { x: 0, y: 0 },

  panelTab: 'photos',
  panelOpen: false,
  sheetFocus: false,

  editingTextId: null,
  cropElementId: null,
  photoEditorId: null,
  adjustmentsClipboard: null,
  replaceForElementId: null,

  dirty: false,
  saveState: 'idle',
  conflict: null,

  uploads: {},
  photoDrag: null,
  uploadRecovery: 'idle',
  userFonts: [],
  shareLinks: [],

  layoutScope: 'spread',
  layoutPage: 'left',
  photoFilter: 'all',
  photoSort: 'date',
  photoTag: null,
  traySelection: [],

  init: ({ albumId, doc, version, photos: rawPhotos, title, album }) => {
    const photos = rawPhotos.map(normalizePhoto);
    const history = new History(doc);
    for (const item of Object.values(get().uploads)) {
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    }
    set({
      uploads: {},
      uploadRecovery: 'idle',
      ready: true,
      albumId,
      album: album ?? null,
      title,
      doc,
      version,
      history,
      canUndo: false,
      canRedo: false,
      photos: Object.fromEntries(photos.map((p) => [p.id, p])),
      photoIds: photos.map((p) => p.id),
      photoTray: sortPhotoTray(photos.map((p) => ({ key: `photo:${p.id}`, photoId: p.id, name: p.name ?? undefined })), Object.fromEntries(photos.map((p) => [p.id, p])), get().photoSort),
      currentTarget: 0,
      selection: { target: 0, ids: [] },
      panelTab: 'photos',
      panelOpen: false,
      sheetFocus: false,
      dirty: false,
      saveState: 'saved',
      conflict: null,
      editingTextId: null,
      cropElementId: null,
      photoEditorId: null,
      replaceForElementId: null,
      traySelection: [],
      photoTag: null,
    });
  },

  apply: (cmd, opts) => {
    const { history } = get();
    let next: AlbumDocument;
    try {
      next = history.apply(cmd, opts);
    } catch (err) {
      // Command errors are programming errors, not user errors: keep the document untouched.
      if (process.env.NODE_ENV !== 'production') console.error('command failed', cmd, err);
      return;
    }
    if (next === get().doc) return;
    set({ doc: next, canUndo: history.canUndo, canRedo: history.canRedo, dirty: true, saveState: 'idle' });
  },

  endCoalescing: () => get().history.endCoalescing(),

  undo: () => {
    const { history } = get();
    const doc = history.undo();
    if (!doc) return;
    set({ doc, canUndo: history.canUndo, canRedo: history.canRedo, dirty: true, saveState: 'idle', editingTextId: null, cropElementId: null });
    pruneSelection(set, get);
  },

  redo: () => {
    const { history } = get();
    const doc = history.redo();
    if (!doc) return;
    set({ doc, canUndo: history.canUndo, canRedo: history.canRedo, dirty: true, saveState: 'idle', editingTextId: null, cropElementId: null });
    pruneSelection(set, get);
  },

  resetDocument: (doc, version) => {
    const history = get().history;
    history.reset(doc);
    const previousTarget = get().currentTarget;
    const currentTarget = previousTarget === 'cover' ? (doc.cover.enabled ? 'cover' : 0) : Math.min(previousTarget, doc.spreads.length - 1);
    set({
      doc,
      version,
      canUndo: false,
      canRedo: false,
      dirty: false,
      saveState: 'saved',
      conflict: null,
      currentTarget,
      selection: { target: currentTarget, ids: [] },
      editingTextId: null,
      cropElementId: null,
    });
  },

  setFirstPageSingle: (on) => {
    const { doc, history } = get();
    if (doc.firstPageSingle === on) return;
    const next = normalizeSingles({ ...doc, firstPageSingle: on });
    history.reset(next);
    set({ doc: next, canUndo: false, canRedo: false, dirty: true, saveState: 'idle' });
  },

  setTitle: (title) => set({ title, dirty: true, saveState: 'idle' }),

  setTarget: (target) =>
    set((s) => {
      // The tool panel follows the surface: arriving at the cover shows the cover tools, leaving it
      // with those tools still up goes back to the page list. A photo tray stays put either way,
      // because the person is probably mid-drag.
      const entering = target === 'cover' && s.currentTarget !== 'cover' && s.panelTab !== 'photos';
      const leaving = target !== 'cover' && s.currentTarget === 'cover' && s.panelTab === 'cover';
      return {
        currentTarget: target,
        selection: { target, ids: [] },
        editingTextId: null,
        cropElementId: null,
        zoomMode: s.zoomMode,
        replaceForElementId: null,
        pan: { x: 0, y: 0 },
        ...(entering ? { panelTab: 'cover' as PanelTab } : leaving ? { panelTab: 'pages' as PanelTab } : {}),
      };
    }),

  select: (ids, opts) =>
    set((s) => {
      const target = opts?.target ?? s.currentTarget;
      const base = opts?.additive && s.selection.target === target ? s.selection.ids : [];
      const merged = Array.from(new Set([...base, ...ids]));
      // Selecting one thing switches the panel to the tab that edits it, so tapping a text box
      // while the photo tray is open shows text tools rather than a tray that ignores it. The
      // sheet's open/closed state is left alone: a tap is not a request to cover the canvas.
      const one = merged.length === 1 ? merged[0] : undefined;
      const elements = target === 'cover' ? s.doc.cover.elements : (s.doc.spreads[target]?.elements ?? []);
      const kind = one ? elements.find((el) => el.id === one)?.type : undefined;
      const panelTab: PanelTab | undefined =
        kind === 'text' ? 'text' : kind === 'photo' ? 'photos' : kind === 'sticker' ? 'stickers' : undefined;
      return { selection: { target, ids: merged }, currentTarget: target, replaceForElementId: null, ...(panelTab ? { panelTab } : {}) };
    }),

  toggleSelect: (id) =>
    set((s) => {
      const has = s.selection.ids.includes(id);
      return { selection: { target: s.selection.target, ids: has ? s.selection.ids.filter((x) => x !== id) : [...s.selection.ids, id] } };
    }),

  clearSelection: () =>
    set((s) => ({ selection: { target: s.currentTarget, ids: [] }, editingTextId: null, cropElementId: null, replaceForElementId: null })),

  setZoom: (zoom) => set({ zoom: clampZoom(zoom), zoomMode: 'manual' }),
  setFitZoom: (fitZoom) => set((s) => (s.zoomMode === 'fit' ? { fitZoom, zoom: fitZoom } : { fitZoom })),
  zoomBy: (factor) => set((s) => ({ zoom: clampZoom(s.zoom * factor), zoomMode: 'manual' })),
  zoomToFit: () => set((s) => ({ zoom: s.fitZoom, zoomMode: 'fit', pan: { x: 0, y: 0 } })),
  setPan: (pan) => set({ pan }),

  setPanelTab: (panelTab) => set({ panelTab, panelOpen: true }),
  setPanelOpen: (panelOpen) => set({ panelOpen }),
  setSheetFocus: (sheetFocus) => set({ sheetFocus }),

  // Editing happens in place on the canvas. The text tab is made current so the sheet, if and
  // when the person opens it, shows the right tools — but it is not opened for them: on a phone
  // the sheet plus the keyboard would leave no album on screen at all.
  setEditingText: (editingTextId) =>
    set(editingTextId ? { editingTextId, cropElementId: null, panelTab: 'text' } : { editingTextId, cropElementId: null }),
  setCropElement: (cropElementId) => set({ cropElementId, editingTextId: null }),
  setPhotoEditor: (photoEditorId) => set(photoEditorId ? { photoEditorId, cropElementId: null, editingTextId: null } : { photoEditorId }),
  setAdjustmentsClipboard: (adjustmentsClipboard) => set({ adjustmentsClipboard }),
  setReplaceFor: (replaceForElementId) => set((s) => ({
    replaceForElementId,
    traySelection: replaceForElementId ? [] : s.traySelection,
    cropElementId: replaceForElementId ? null : s.cropElementId,
    panelTab: replaceForElementId ? 'photos' : s.panelTab,
    panelOpen: replaceForElementId ? true : s.panelOpen,
  })),

  markSaving: () => set({ saveState: 'saving' }),
  markSaved: (version) => set({ version, dirty: false, saveState: 'saved' }),
  markSaveError: () => set({ saveState: 'error' }),
  setConflict: (conflict) => set({ conflict, saveState: conflict ? 'conflict' : 'idle' }),

  setUpload: (item) => set((s) => queueTrayUpload(s, item)),
  clearUpload: (id) =>
    set((s) => {
      const { [id]: _drop, ...rest } = s.uploads;
      if (_drop?.previewUrl) URL.revokeObjectURL(_drop.previewUrl);
      const keepPhoto = _drop?.photoId && s.photos[_drop.photoId];
      const photoTray = keepPhoto ? s.photoTray : s.photoTray.filter((entry) => entry.uploadId !== id);
      return { uploads: rest, photoTray, traySelection: s.traySelection.filter((key) => photoTray.some((entry) => entry.key === key)) };
    }),

  setPhotos: (photos) => set((s) => mergeTrayPhotos(s, photos)),

  upsertPhoto: (photo) => set((s) => mergeTrayPhotos(s, [photo], false)),

  removePhoto: (id) =>
    set((s) => {
      const { [id]: _drop, ...photos } = s.photos;
      const photoTray = s.photoTray.filter((entry) => entry.photoId !== id || (entry.uploadId && s.uploads[entry.uploadId]));
      return { photos, photoIds: s.photoIds.filter((x) => x !== id), photoTray, traySelection: s.traySelection.filter((key) => photoTray.some((entry) => entry.key === key && entry.photoId !== id)) };
    }),

  setPhotoDrag: (photoDrag) => set({ photoDrag }),
  setUserFonts: (userFonts) => set({ userFonts }),
  setShareLinks: (shareLinks) => set({ shareLinks }),
  setLayoutScope: (layoutScope) => set({ layoutScope }),
  setLayoutPage: (layoutPage) => set({ layoutPage }),
  setPhotoFilter: (photoFilter) => set({ photoFilter }),
  setPhotoTag: (photoTag) => set({ photoTag }),
  applyPhotoTags: (updates) =>
    set((s) => {
      let photos = s.photos;
      let changed = false;
      for (const update of updates) {
        const photo = photos[update.id];
        if (!photo || sameTags(photo.tags, update.tags)) continue;
        if (!changed) {
          photos = { ...photos };
          changed = true;
        }
        photos[update.id] = { ...photo, tags: update.tags };
      }
      return changed ? { photos } : {};
    }),
  setPhotoSort: (photoSort) => set((s) => ({ photoSort, photoTray: sortPhotoTray(s.photoTray, s.photos, photoSort) })),
  toggleTraySelection: (id, additive = true) =>
    set((s) => {
      const entry = s.photoTray.find((entry) => entry.key === id);
      if (!entry || !canSelectTrayEntry(s, entry)) return s;
      if (!additive) return { traySelection: [id] };
      return { traySelection: s.traySelection.includes(id) ? s.traySelection.filter((x) => x !== id) : [...s.traySelection, id] };
    }),
  clearTraySelection: () => set({ traySelection: [] }),
  selectTrayPhotos: (ids) => set((s) => ({
    traySelection: [...new Set([...s.traySelection, ...ids])].filter((key) => {
      const entry = s.photoTray.find((entry) => entry.key === key);
      return entry && canSelectTrayEntry(s, entry);
    }),
  })),
}));

function clampZoom(z: number) {
  return Math.min(ZOOM_LIMITS.max, Math.max(ZOOM_LIMITS.min, z));
}

/** After undo/redo the selected ids may no longer exist. */
function pruneSelection(set: (partial: Partial<EditorState>) => void, get: () => EditorState) {
  const { selection, doc } = get();
  if (selection.ids.length === 0) return;
  const els = selection.target === 'cover' ? doc.cover.elements : (doc.spreads[selection.target]?.elements ?? []);
  const alive = new Set(els.map((e) => e.id));
  const ids = selection.ids.filter((id) => alive.has(id));
  if (ids.length !== selection.ids.length) set({ selection: { target: selection.target, ids } });
}

// ── selectors ───────────────────────────────────────────────────────────────

export function elementsOf(doc: AlbumDocument, target: Target): Element[] {
  return target === 'cover' ? doc.cover.elements : (doc.spreads[target]?.elements ?? []);
}

export function selectedElements(state: { doc: AlbumDocument; selection: Selection }): Element[] {
  const els = elementsOf(state.doc, state.selection.target);
  const ids = new Set(state.selection.ids);
  return els.filter((e) => ids.has(e.id));
}

/** How many times each photo is used anywhere in the document. */
export function photoUsage(doc: AlbumDocument): Record<string, number> {
  const counts: Record<string, number> = {};
  const bump = (id: string | null | undefined) => {
    if (id) counts[id] = (counts[id] ?? 0) + 1;
  };
  const visit = (els: Element[]) => {
    for (const el of els) if (el.type === 'photo') bump(el.photoId);
  };
  visit(doc.cover.elements);
  if (doc.cover.background.type === 'photo') bump(doc.cover.background.photoId);
  for (const s of doc.spreads) {
    visit(s.elements);
    if (s.background.type === 'photo') bump(s.background.photoId);
    if (s.backgroundRight?.type === 'photo') bump(s.backgroundRight.photoId);
  }
  return counts;
}

export const usedPhotoIds = (doc: AlbumDocument) => referencedPhotoIds(doc);
